import type { BackendMessagePart } from '../../state/types'
import { authFetch, buildAgentUrl, buildWorkspaceUrl, throwApiError, unwrapArray } from './core'
import type { ClassroomStatus, MemberRole } from './models'
import { requestOptions } from './workspace'

export type AgeTier = 'UNDER_13' | 'TEEN'

export interface StudentItem {
  id: string
  userId: string
  name: string
  username: string
  displayPassword?: string | null
  mascotId?: string | null
  mascotLabel?: string | null
  mascotEmoji?: string | null
  pictureSecretIds?: string[] | null
  pictureSecretEmojis?: string[] | null
  givenName: string
  surName: string
  email: string
  avatarUrl: string | null
  role: MemberRole
  type: string
  status: string
  isActive: boolean
  isOwner: boolean
  joinedAt: string
  lastAccessedAt: string
  manageCaseCount: number
  workspaceName: string
  phoneNumber: string | null
  organisation: string | null
  ageTier: AgeTier | null
  birthDay?: string | null
  classes?: MemberDirectoryClassSummary[]
  classIds?: string[]
  classTitles?: string[]
  classCount?: number
  credits?: number | null
}

export interface MemberDirectoryClassSummary {
  id: string
  title: string
  status: ClassroomStatus
}

export interface StudentsResponse {
  content: StudentItem[]
  totalElements: number
  totalPages: number
  size: number
  number: number
}

export interface CreateMemberPayload {
  givenName: string
  surName: string
  username?: string
  initialPassword?: string
  ageTier?: AgeTier | null
  role?: MemberRole
}

export type CreateStudentPayload = CreateMemberPayload

export interface BulkCreateStudentItem {
  givenName: string
  surName: string
  username?: string
  email?: string
  initialPassword?: string
  phoneNumber?: string
  dateOfBirth?: string
  ageTier?: AgeTier | null
}

export interface BulkCreateStudentsPayload {
  defaultAgeTier?: AgeTier | null
  students: BulkCreateStudentItem[]
}

export interface BulkCreateFailure {
  index?: number
  row?: number
  reason?: string
  message?: string
  [key: string]: unknown
}

export interface BulkCreateStudentsResponse {
  total: number
  createdCount: number
  failedCount: number
  created: StudentItem[]
  failures?: BulkCreateFailure[]
}

export interface FlaggedMessage {
  id: string
  chatId: string
  role: string
  content?: string
  parts?: BackendMessagePart[]
  createdAt?: string
  timestamp?: string
  moderationAction?: string
  moderationCategory?: string
  originalContent?: string
}

export async function fetchMembers(
  token: string,
  role: MemberRole,
  page = 0,
  size = 10,
  keyword?: string,
): Promise<StudentsResponse> {
  if (role === 'STUDENT' || role === 'TEACHER') {
    const agentUrl = new URL(buildAgentUrl(`/${role === 'STUDENT' ? 'students' : 'teachers'}`))
    agentUrl.searchParams.set('page', String(page))
    agentUrl.searchParams.set('size', String(size))
    if (keyword?.trim()) {
      agentUrl.searchParams.set('keyword', keyword.trim())
    }

    const agentResponse = await authFetch(agentUrl.toString(), requestOptions(token))
    if (agentResponse.ok) {
      return agentResponse.json() as Promise<StudentsResponse>
    }
  }

  const url = new URL(buildWorkspaceUrl('/api/workspace/users/workspaces/members'))
  url.searchParams.set('page', String(page))
  url.searchParams.set('size', String(size))
  url.searchParams.set('role', role)
  if (keyword?.trim()) {
    url.searchParams.set('keyword', keyword.trim())
  }

  const response = await authFetch(url.toString(), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch members')
  }
  return response.json() as Promise<StudentsResponse>
}

export const fetchStudents = (
  token: string,
  page?: number,
  size?: number,
  keyword?: string,
) => fetchMembers(token, 'STUDENT', page, size, keyword)

export async function fetchAllMembers(
  token: string,
  role: MemberRole,
  keyword?: string,
  pageSize = 100,
): Promise<StudentItem[]> {
  const results: StudentItem[] = []
  const seen = new Set<string>()
  let page = 0
  let totalPages = 1

  while (page < totalPages) {
    const response = await fetchMembers(token, role, page, pageSize, keyword)

    for (const item of response.content) {
      const key = item.userId || item.id
      if (seen.has(key)) continue
      seen.add(key)
      results.push(item)
    }

    totalPages = Math.max(response.totalPages || 0, page + 1)
    page += 1

    if (response.content.length === 0) break
  }

  return results
}

export async function updateMember(
  token: string,
  memberId: string,
  data: { name?: string; givenName?: string; surName?: string; username?: string; ageTier?: AgeTier | null; newPassword?: string },
): Promise<StudentItem> {
  const response = await authFetch(
    buildAgentUrl(`/members/${memberId}`),
    {
      method: 'PATCH',
      ...requestOptions(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    },
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to update member')
  }
  return response.json() as Promise<StudentItem>
}

export async function toggleMemberStatus(
  token: string,
  memberId: string,
  enable: boolean,
): Promise<void> {
  const memberStatus = enable ? 'ACTIVE' : 'DEACTIVATED'
  const response = await authFetch(
    buildAgentUrl(`/members/${memberId}/status`),
    {
      method: 'PATCH',
      ...requestOptions(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({ memberStatus }),
    },
  )
  if (!response.ok) {
    await throwApiError(response, `Failed to ${enable ? 'enable' : 'disable'} member`)
  }
}

export async function createMember(
  token: string,
  data: CreateMemberPayload,
): Promise<StudentItem> {
  const body: Record<string, unknown> = {
    givenName: data.givenName,
    surName: data.surName,
    role: data.role ?? 'STUDENT',
  }
  if (data.username !== undefined) {
    body.username = data.username
  }
  if (data.initialPassword !== undefined) {
    body.initialPassword = data.initialPassword
  }
  if (data.ageTier !== undefined) {
    body.ageTier = data.ageTier
  }

  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces/members'),
    {
      method: 'POST',
      ...requestOptions(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    },
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to create member')
  }
  return response.json() as Promise<StudentItem>
}

export async function createStudent(
  token: string,
  data: CreateStudentPayload,
): Promise<StudentItem> {
  const body: Record<string, unknown> = {
    givenName: data.givenName,
    surName: data.surName,
  }
  if (data.username !== undefined) {
    body.username = data.username
  }
  if (data.initialPassword !== undefined) {
    body.initialPassword = data.initialPassword
  }
  if (data.ageTier !== undefined) {
    body.ageTier = data.ageTier
  }

  const response = await authFetch(buildAgentUrl('/students'), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to create student')
  }
  return response.json() as Promise<StudentItem>
}

export async function createTeacher(
  token: string,
  data: CreateMemberPayload,
): Promise<StudentItem> {
  const body: Record<string, unknown> = {
    givenName: data.givenName,
    surName: data.surName,
  }
  if (data.username !== undefined) {
    body.username = data.username
  }
  if (data.initialPassword !== undefined) {
    body.initialPassword = data.initialPassword
  }
  if (data.ageTier !== undefined) {
    body.ageTier = data.ageTier
  }

  const response = await authFetch(buildAgentUrl('/teachers'), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to create teacher')
  }
  return response.json() as Promise<StudentItem>
}

export async function createStudentsBulk(
  token: string,
  data: BulkCreateStudentsPayload,
): Promise<BulkCreateStudentsResponse> {
  const response = await authFetch(buildAgentUrl('/students/bulk'), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to bulk create students')
  }
  return response.json() as Promise<BulkCreateStudentsResponse>
}

export async function createStudentsBulkFromCsv(
  token: string,
  file: File,
  defaultAgeTier?: AgeTier | null,
): Promise<BulkCreateStudentsResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (defaultAgeTier) {
    formData.append('defaultAgeTier', defaultAgeTier)
  }

  const response = await authFetch(buildAgentUrl('/students/bulk/csv'), {
    method: 'POST',
    body: formData,
    ...requestOptions(token),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to bulk create students from CSV')
  }
  return response.json() as Promise<BulkCreateStudentsResponse>
}

export async function downloadStudentBulkCsvTemplate(token: string): Promise<Blob> {
  const response = await authFetch(buildAgentUrl('/students/bulk/csv/template'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to download student CSV template')
  }
  return response.blob()
}

export async function fetchFlaggedMessages(
  token: string,
  chatId: string,
): Promise<FlaggedMessage[]> {
  const response = await authFetch(
    buildAgentUrl(`/admin/chats/${chatId}/messages`),
    requestOptions(token),
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch flagged messages')
  }

  const json = await response.json()
  return unwrapArray<FlaggedMessage>(json, ['messages', 'data'])
}
